import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTicketAlt, FaChartLine, FaCalendarAlt, FaMoneyBillWave, FaClock, FaStar } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import { logout } from '../../../../utils/api/auth';
import '../../AdminDash/css/AdminDash.css';
import '../../../../components/css/AdminAnalytics.css';

function TicketSalesAnalytics() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState({
    totalTicketsSold: 0,
    totalRevenue: 0,
    averageTicketPrice: 0,
    bestSellingCategory: '',
    salesByCategory: [],
    salesTrends: [],
    pricingAnalytics: [],
    timeBasedSales: [],
    topSellingEvents: []
  });

  useEffect(() => {
    fetchTicketSalesData();
  }, []);

  const fetchTicketSalesData = async () => {
    try {
      setLoading(true);
      
      // Mock data for demonstration
      const mockData = {
        totalTicketsSold: 45678,
        totalRevenue: 12450000,
        averageTicketPrice: 272.50,
        bestSellingCategory: 'Concert',
        salesByCategory: [
          { category: 'Concert', tickets: 18500, revenue: 5800000, avgPrice: 313.51, growth: 18.5 },
          { category: 'Sports', tickets: 12300, revenue: 3200000, avgPrice: 260.16, growth: 12.3 },
          { category: 'Conference', tickets: 8900, revenue: 2450000, avgPrice: 275.28, growth: 8.7 },
          { category: 'Comedy', tickets: 5980, revenue: 1050000, avgPrice: 175.59, growth: 22.1 },
        ],
        salesTrends: [
          { month: 'Jan', tickets: 6200, revenue: 1650000, avgPrice: 266.13 },
          { month: 'Feb', tickets: 7100, revenue: 1950000, avgPrice: 274.65 },
          { month: 'Mar', tickets: 7800, revenue: 2180000, avgPrice: 279.49 },
          { month: 'Apr', tickets: 8200, revenue: 2350000, avgPrice: 286.59 },
          { month: 'May', tickets: 8900, revenue: 2680000, avgPrice: 301.12 },
          { month: 'Jun', tickets: 7478, revenue: 2140000, avgPrice: 286.35 },
        ],
        pricingAnalytics: [
          { priceRange: '0-100', tickets: 8900, revenue: 580000, percentage: 19.5 },
          { priceRange: '100-200', tickets: 12300, revenue: 1850000, percentage: 26.9 },
          { priceRange: '200-300', tickets: 15600, revenue: 3900000, percentage: 34.2 },
          { priceRange: '300-500', tickets: 6800, revenue: 2720000, percentage: 14.9 },
          { priceRange: '500+', tickets: 2078, revenue: 3400000, percentage: 4.5 },
        ],
        timeBasedSales: [
          { time: 'Morning (6AM-12PM)', tickets: 5800, percentage: 12.7 },
          { time: 'Afternoon (12PM-6PM)', tickets: 18900, percentage: 41.4 },
          { time: 'Evening (6PM-12AM)', tickets: 18978, percentage: 41.5 },
          { time: 'Night (12AM-6AM)', tickets: 2000, percentage: 4.4 },
        ],
        topSellingEvents: [
          { name: 'Summer Music Festival', tickets: 4500, revenue: 1350000, category: 'Concert', date: '2026-06-15' },
          { name: 'Tech Summit 2026', tickets: 2800, revenue: 840000, category: 'Conference', date: '2026-05-20' },
          { name: 'Champions League Final', tickets: 3500, revenue: 1050000, category: 'Sports', date: '2026-06-01' },
          { name: 'Comedy Night Special', tickets: 1800, revenue: 360000, category: 'Comedy', date: '2026-05-28' },
        ]
      };

      setAnalyticsData(mockData);
    } catch (error) {
      console.error('Error fetching ticket sales data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout(nav);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="payment-analytics">
        <GlobalHeader
          title="Ticket Sales Analytics"
          subtitle="Loading ticket sales data..."
          onLogout={handleLogout}
          backTo="/admin"
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-analytics">
      <GlobalHeader
        title="Ticket Sales Analytics"
        subtitle="Detailed ticket sales analysis by type, price, and timing"
        onLogout={handleLogout}
        backTo="/admin"
      />

      <main className="payment-analytics__main">
        {/* Overview Cards */}
        <section className="analytics-overview">
          <div className="overview-card">
            <div className="card-icon">
              <FaTicketAlt />
            </div>
            <div className="card-content">
              <h3>Total Tickets Sold</h3>
              <p className="card-value">{analyticsData.totalTicketsSold.toLocaleString()}</p>
              <p className="card-subtitle">All time sales</p>
            </div>
          </div>

          <div className="overview-card">
            <div className="card-icon">
              <FaMoneyBillWave />
            </div>
            <div className="card-content">
              <h3>Total Revenue</h3>
              <p className="card-value">{formatCurrency(analyticsData.totalRevenue)}</p>
              <p className="card-subtitle">From ticket sales</p>
            </div>
          </div>

          <div className="overview-card">
            <div className="card-icon">
              <FaChartLine />
            </div>
            <div className="card-content">
              <h3>Avg Ticket Price</h3>
              <p className="card-value">{formatCurrency(analyticsData.averageTicketPrice)}</p>
              <p className="card-subtitle">Per ticket</p>
            </div>
          </div>

          <div className="overview-card">
            <div className="card-icon">
              <FaStar />
            </div>
            <div className="card-content">
              <h3>Best Category</h3>
              <p className="card-value">{analyticsData.bestSellingCategory}</p>
              <p className="card-subtitle">Top performing</p>
            </div>
          </div>
        </section>

        {/* Sales by Category */}
        <section className="sales-category-section">
          <h3>Sales by Event Category</h3>
          <div className="category-table">
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Tickets Sold</th>
                  <th>Revenue</th>
                  <th>Avg Price</th>
                  <th>Growth</th>
                  <th>Market Share</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData.salesByCategory.map((category, index) => (
                  <tr key={index}>
                    <td className="category-name">
                      <span className="category-badge">{category.category}</span>
                    </td>
                    <td>{category.tickets.toLocaleString()}</td>
                    <td>{formatCurrency(category.revenue)}</td>
                    <td>{formatCurrency(category.avgPrice)}</td>
                    <td>
                      <span className={`growth-indicator ${category.growth > 15 ? 'high' : category.growth > 10 ? 'medium' : 'low'}`}>
                        +{category.growth}%
                      </span>
                    </td>
                    <td>
                      <div className="market-share">
                        <div className="share-bar" style={{ width: `${(category.tickets / analyticsData.totalTicketsSold) * 100}%` }}></div>
                        <span className="share-text">{Math.round((category.tickets / analyticsData.totalTicketsSold) * 100)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Charts Section */}
        <section className="analytics-charts">
          <div className="chart-container">
            <h3>Sales Trends (6 Months)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analyticsData.salesTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="tickets" stroke="#667eea" strokeWidth={2} name="Tickets Sold" />
                <Line yAxisId="right" type="monotone" dataKey="avgPrice" stroke="#f5576c" strokeWidth={2} name="Avg Price" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-container">
            <h3>Pricing Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analyticsData.pricingAnalytics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="priceRange" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="tickets" fill="#00b894" name="Tickets Sold" />
                <Bar dataKey="revenue" fill="#f7b731" name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Time-Based Sales */}
        <section className="time-sales-section">
          <h3>Sales by Time of Day</h3>
          <div className="time-cards">
            {analyticsData.timeBasedSales.map((timeSlot, index) => (
              <div key={index} className="time-card">
                <div className="time-header">
                  <FaClock className="time-icon" />
                  <h4>{timeSlot.time}</h4>
                </div>
                <div className="time-metrics">
                  <div className="metric">
                    <span className="metric-value">{timeSlot.tickets.toLocaleString()}</span>
                    <span className="metric-label">tickets</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">{timeSlot.percentage}%</span>
                    <span className="metric-label">of total</span>
                  </div>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${timeSlot.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Top Selling Events */}
        <section className="top-events-section">
          <h3>Top Selling Events</h3>
          <div className="events-grid">
            {analyticsData.topSellingEvents.map((event, index) => (
              <div key={index} className="event-card">
                <div className="event-header">
                  <h4>{event.name}</h4>
                  <span className="event-category">{event.category}</span>
                </div>
                <div className="event-metrics">
                  <div className="metric-row">
                    <span className="metric-label">Tickets:</span>
                    <span className="metric-value">{event.tickets.toLocaleString()}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Revenue:</span>
                    <span className="metric-value">{formatCurrency(event.revenue)}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Date:</span>
                    <span className="metric-value">{event.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing Insights */}
        <section className="pricing-insights">
          <h3>Pricing Insights</h3>
          <div className="insights-grid">
            <div className="insight-card positive">
              <div className="insight-icon">
                <FaChartLine />
              </div>
              <div className="insight-content">
                <h4>Sweet Spot: R200-300</h4>
                <p>34.2% of tickets sold in R200-300 range, generating highest revenue volume</p>
              </div>
            </div>
            
            <div className="insight-card positive">
              <div className="insight-icon">
                <FaCalendarAlt />
              </div>
              <div className="insight-content">
                <h4>Peak Sales Hours</h4>
                <p>Afternoon and evening sales account for 83% of total ticket purchases</p>
              </div>
            </div>
            
            <div className="insight-card neutral">
              <div className="insight-icon">
                <FaMoneyBillWave />
              </div>
              <div className="insight-content">
                <h4>Premium Performance</h4>
                <p>High-end tickets (R500+) represent only 4.5% of sales but 27.3% of revenue</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default TicketSalesAnalytics;
